/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState, useEffect, useMemo } from 'react';
import { getAPIType } from 'utils/APITypes';
import ApiHandlerProvider from 'contexts/ApiHandler';
import { useCreateApiHandler } from 'hooks/createApiHandler';
import { Route, useParams } from 'react-router-dom';
import Routing from 'components/Routing';
import ExplorerBody from 'components/ExplorerBody';
import { apiPath, getApi } from 'utils/navigation';
import { useConfig } from 'contexts/config';
import { InlineLoading, ToastNotification, SkipToContent } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { decycle } from 'json-decycle';
import { handleKeyDown } from 'utils/common';

// This disables the react-hooks/rules-of-hooks linting check because it is incorrectly flagging issues using
// use* hooks inside callbacks. They are actually being used correctly, in components defined under the Routes
// but the linter isn't able to determine this.
/* eslint react-hooks/rules-of-hooks: 0 */

const getFromOptions = (entry, api, apis, options) => {
  let result = null;
  if (api && apis && options && options[entry]) {
    const apiIndex = apis.indexOf(api);
    result = options[entry][apiIndex] || null;
  }
  return result;
};

const DEFAULT_API = { name: 'channelA', asyncapi: true, info: { title: 'Channel A API' } };
const buildApiState = (options, apis = [], api = apis[0], defaultApi = DEFAULT_API) => {
  if (!api) {
   console.warn('Falling back to default API:', defaultApi.name);
    api = defaultApi;
  }
  return {
    currentApi: JSON.parse(JSON.stringify(api, decycle())),
        apiType: getAPIType(api),
   infoSectionEntries: getFromOptions('infoSectionEntries', api, apis, options),
    documentationEntries: getFromOptions('documentationEntries', api, apis, options)
 };
};

const getErrorProps = (error, prop) => {
  if (error && error.type === 'external_ref') {
    if (prop === 'title') {
      return 'external_ref_error';
    }
    if (prop === 'subtitle') {
      return 'external_ref_error_description';
    }
  } else {
    if (prop === 'title') {
      return 'async_parse_errors';
    }
    if (prop === 'subtitle') {
      return 'async_parse_errors_description';
    }
  }
};

function App() {
  return (
    <Route
      path={['/:entity/*', '/']}
      // eslint-disable-next-line react/no-unstable-nested-components
      component={({ location }) => {
        const pathParams = useParams();

  const entityIsApi = useMemo(() => pathParams.entity && pathParams.entity !== 'documentation', [pathParams]);
  const { apis, options } = useConfig();
  const [apiState, updateApiState] = useState(() => buildApiState(apis, options, entityIsApi ? getApi(apis, pathParams.entity) : apis[0], DEFAULT_API));
  const { originalApi, apiHandler, loading, error: parseError } = useCreateApiHandler(apiState.currentApi);
  const { t } = useTranslation();

  useEffect(() => {
    updateApiState(buildApiState(apis, options, entityIsApi ? getApi(apis, pathParams.entity) : apis[0], DEFAULT_API));
  }, [apis, options, entityIsApi, pathParams.entity]);

        useEffect(() => {
          try {
            const { onReady } = options;
            if (typeof onReady === 'string') {
              window[onReady]();
            } else if (typeof onReady === 'function') {
              onReady();
            }
          } catch (e) {
            console.error('onReady callback failed:', e);
          }
        }, [options.onReady]);

        useEffect(() => {
          if (entityIsApi) {
            const newApi = getApi(apis, pathParams.entity);

            // only call for new api if we know api path param has changed
            if (apiPath(apiState.currentApi) !== apiPath(newApi)) {
              updateApiState(buildApiState(options, apis, newApi));
            }
          }
        }, [pathParams, apis, pathParams.entity, apiState.currentApi, entityIsApi, options]);

        let jsx;

        if (loading) {
          jsx = <InlineLoading />;
        } else if (parseError && (parseError.type === 'external_ref' || !location.pathname.includes('parseerror'))) {
          jsx = (
            <ToastNotification
              kind="error"
              role="alert"
              title={t(getErrorProps(parseError, 'title'))}
              subtitle={t(getErrorProps(parseError, 'subtitle'))}
              className="errorFrame"
              hideCloseButton
            >
              <pre className="validationErrorText">
                <code>{parseError.errorString}</code>
              </pre>
            </ToastNotification>
          );
        } else {
          jsx = <Routing apiState={apiState} />;
        }

        const shouldSplit = apiState.apiType !== 'GraphQL';
        const skipToContentHref = `${window.location.origin}${location.pathname}${location.search}#main-explorer-content`;

        return (
          <ApiHandlerProvider api={originalApi} handler={apiHandler} loading={loading} error={parseError?.errorString}>
            <SkipToContent
              href={skipToContentHref}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation();

                document?.getElementById('main-explorer-content')?.focus();
              }}
              onKeyDown={handleKeyDown(() => document?.getElementById('main-explorer-content')?.focus())}
            />
            <ExplorerBody splitView={shouldSplit}>{jsx}</ExplorerBody>
          </ApiHandlerProvider>
        );
      }}
    />
  );
}

export default App;
